"use client";

import Image from "next/image";
import type { CSSProperties, RefObject } from "react";
import {
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  FROZEN_DELIVERY_QUOTE_PRODUCTS,
  buildFrozenDeliveryQuoteBoxPreviewLayout,
  buildFrozenDeliveryQuoteSummary,
  capacityLabelForUnits,
  capacityPercentForUnits,
  createFrozenDeliveryQuoteEmptyQuantities,
  getFrozenDeliveryQuoteBoxTabState,
  type FrozenDeliveryQuoteBox,
  type FrozenDeliveryQuoteBoxTab,
  type FrozenDeliveryQuoteProduct,
  type FrozenDeliveryQuoteProductId,
  type FrozenDeliveryQuoteQuantities,
  type FrozenDeliveryQuoteSummary,
  updateFrozenDeliveryQuoteQuantity,
} from "@/lib/frozen-delivery-quote";
import {
  buildFrozenDeliveryResultCopy,
  resolveFrozenDeliveryCalculatorIntro,
} from "@/lib/frozen-delivery-quote-content";
import type { MediaAsset } from "@/lib/storefront-types";

type FrozenDeliveryQuotePageProps = {
  bodyHtml?: string | null;
  eyebrow?: string | null;
  headingLevel?: 1 | 2;
  headline?: string | null;
  image?: MediaAsset | null;
  pricePerBox: number | null;
};

type FrozenDeliveryQuoteViewState = "intro" | "quote";

type FrozenDeliveryQuoteProductCardProps = {
  onAddItem: (productId: FrozenDeliveryQuoteProductId) => void;
  isPending: boolean;
  product: FrozenDeliveryQuoteProduct;
  quantity: number;
};

const POLY_BOX_IMAGE_SRC = "/images/delivery-quote/poly-box.png";
const DEFAULT_PREVIEW_SMOOTHING_PERCENT = 23;

function ArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      {direction === "left" ? (
        <path d="M15 18l-6-6 6-6" />
      ) : (
        <path d="M9 18l6-6-6-6" />
      )}
    </svg>
  );
}

export function FrozenDeliveryQuotePage({
  bodyHtml,
  eyebrow,
  headingLevel = 2,
  headline,
  image,
  pricePerBox,
}: FrozenDeliveryQuotePageProps) {
  const [viewState, setViewState] =
    useState<FrozenDeliveryQuoteViewState>("intro");
  /*
  const [previewSmoothingPercent, setPreviewSmoothingPercent] = useState(
    DEFAULT_PREVIEW_SMOOTHING_PERCENT,
  );
  */
  const [quantities, setQuantities] = useState(() =>
    createFrozenDeliveryQuoteEmptyQuantities(),
  );
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState(0);
  const [selectedTabSelection, setSelectedTabSelection] = useState<{
    index: number;
    signature: string;
  } | null>(null);
  const deferredQuantities = useDeferredValue(quantities);
  const [isPending, startTransition] = useTransition();
  const experienceRef = useRef<HTMLElement>(null);
  const addItemsButtonRef = useRef<HTMLButtonElement>(null);
  const itemModalCloseRef = useRef<HTMLButtonElement>(null);
  const summary = buildFrozenDeliveryQuoteSummary(
    deferredQuantities,
    pricePerBox,
  );
  const tabState = getFrozenDeliveryQuoteBoxTabState(summary);
  const tabStateSignature = tabState.tabs
    .map(
      (tab) =>
        `${tab.label}:${tab.boxIndex ?? "new"}:${tab.isSyntheticEmpty ? "synthetic" : "real"}:${tab.box?.usedUnits ?? 0}:${tab.box?.items.map((item) => item.id).join(",") ?? "empty"}`,
    )
    .join("|");

  useEffect(() => {
    if (!isItemModalOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => itemModalCloseRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }

      setIsItemModalOpen(false);
      window.setTimeout(() => addItemsButtonRef.current?.focus(), 0);
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isItemModalOpen]);

  const selectedTabIndex =
    selectedTabSelection &&
    selectedTabSelection.signature === tabStateSignature
      ? selectedTabSelection.index
      : tabState.activeTabIndex;

  function handleOpenItemModal() {
    setActiveProductIndex(findFirstSelectedProductIndex(quantities));
    setIsItemModalOpen(true);
  }

  function scrollExperienceIntoView() {
    window.requestAnimationFrame(() => {
      experienceRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  function handleGetStarted() {
    setViewState("quote");
    scrollExperienceIntoView();
  }

  function handleCloseItemModal() {
    setIsItemModalOpen(false);
    window.setTimeout(() => addItemsButtonRef.current?.focus(), 0);
  }

  function handleAddItem(productId: FrozenDeliveryQuoteProductId) {
    startTransition(() => {
      setQuantities((current) =>
        updateFrozenDeliveryQuoteQuantity(current, productId, 1),
      );
    });
  }

  function handleRemoveItem(
    productId: FrozenDeliveryQuoteProductId,
  ) {
    startTransition(() => {
      setQuantities((current) =>
        updateFrozenDeliveryQuoteQuantity(current, productId, -1),
      );
    });
  }

  function handleStartAgain() {
    const emptyQuantities = createFrozenDeliveryQuoteEmptyQuantities();
    startTransition(() => {
      setQuantities(emptyQuantities);
    });
    setActiveProductIndex(0);
    setSelectedTabSelection(null);
    setIsItemModalOpen(false);
    setViewState("intro");
    scrollExperienceIntoView();
  }

  return (
    <section
      className="frozen-delivery-quote-page scroll-mt-32 overflow-hidden bg-[#f7fbff] text-slate-950"
      ref={experienceRef}
    >
      <div className="relative isolate">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(circle_at_18%_0%,rgba(var(--storefront-primary-rgb),0.16),transparent_32%),linear-gradient(180deg,#ffffff_0%,#edf7ff_62%,rgba(247,251,255,0)_100%)]" />
          <div className="absolute right-[-9rem] top-12 h-64 w-64 rounded-full bg-[rgba(var(--storefront-primary-rgb),0.12)] blur-3xl" />
          <div className="absolute left-[-12rem] top-64 h-64 w-64 rounded-full bg-cyan-100/70 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          {viewState === "intro" ? (
            <FrozenDeliveryQuoteIntro
              bodyHtml={bodyHtml}
              eyebrow={eyebrow}
              headingLevel={headingLevel}
              headline={headline}
              image={image}
              onGetStarted={handleGetStarted}
              pricePerBoxLabel={summary.formattedPricePerBox}
            />
          ) : (
            <FrozenDeliveryQuoteWorkspace
              addItemsButtonRef={addItemsButtonRef}
              onOpenItems={handleOpenItemModal}
              onRemoveItem={handleRemoveItem}
              onSelectTab={(index) =>
                setSelectedTabSelection({
                  index,
                  signature: tabStateSignature,
                })
              }
              onStartAgain={handleStartAgain}
              selectedTabIndex={selectedTabIndex}
              summary={summary}
              tabs={tabState.tabs}
            />
          )}
        </div>
      </div>

      {isItemModalOpen ? (
        <FrozenDeliveryItemsModal
          activeProductIndex={activeProductIndex}
          closeButtonRef={itemModalCloseRef}
          isPending={isPending}
          onActiveProductIndexChange={setActiveProductIndex}
          onAddItem={handleAddItem}
          onClose={handleCloseItemModal}
          quantities={quantities}
        />
      ) : null}
    </section>
  );
}

function FrozenDeliveryQuoteIntro({
  bodyHtml,
  eyebrow,
  headingLevel,
  headline,
  image,
  onGetStarted,
  pricePerBoxLabel,
}: {
  bodyHtml?: string | null;
  eyebrow?: string | null;
  headingLevel: 1 | 2;
  headline?: string | null;
  image?: MediaAsset | null;
  onGetStarted: () => void;
  pricePerBoxLabel: string | null;
}) {
  const intro = resolveFrozenDeliveryCalculatorIntro(
    {
      bodyHtml,
      eyebrow,
      headline,
      image,
    },
    pricePerBoxLabel,
    POLY_BOX_IMAGE_SRC,
  );
  const HeadlineTag = headingLevel === 1 ? "h1" : "h2";

  return (
    <section className="overflow-hidden rounded-[2.25rem] border border-slate-200/80 bg-white shadow-[0_28px_100px_-70px_rgba(15,23,42,0.65)]">
      <div className="grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-stretch">
        <div className="relative min-h-[18rem] overflow-hidden bg-white sm:min-h-[24rem] lg:min-h-[31rem]">
          <Image
            alt={intro.image.alt}
            className="object-contain p-8 sm:p-10"
            fill
            priority
            sizes="(min-width: 1024px) 42vw, 100vw"
            src={intro.image.url}
            unoptimized
          />
        </div>

        <div className="flex flex-col justify-center p-6 sm:p-9 lg:p-12">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.3em] text-[var(--storefront-primary)]">
            {intro.eyebrow}
          </p>
          <HeadlineTag className="mt-4 max-w-3xl text-[clamp(1.5rem,2.35vw,2.85rem)] font-semibold leading-[1.02] tracking-tight text-slate-950">
            {intro.headline}
          </HeadlineTag>
          <div
            className="mt-6 grid gap-4 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8"
            dangerouslySetInnerHTML={{ __html: intro.bodyHtml }}
          />

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onGetStarted}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[var(--storefront-primary)] px-6 py-3 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-24px_rgba(13,89,242,0.8)] transition-colors hover:bg-slate-950"
            >
              Get Started
            </button>
            <span className="inline-flex min-h-12 items-center rounded-full border border-slate-200 bg-slate-50 px-5 text-sm font-semibold text-slate-700">
              {pricePerBoxLabel
                ? `${pricePerBoxLabel} to deliver each box`
                : "Delivery price currently unavailable"}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FrozenDeliveryQuoteWorkspace({
  addItemsButtonRef,
  onOpenItems,
  onRemoveItem,
  onSelectTab,
  onStartAgain,
  selectedTabIndex,
  summary,
  tabs,
}: {
  addItemsButtonRef: RefObject<HTMLButtonElement | null>;
  onOpenItems: () => void;
  onRemoveItem: (productId: FrozenDeliveryQuoteProductId) => void;
  onSelectTab: (index: number) => void;
  onStartAgain: () => void;
  selectedTabIndex: number;
  summary: FrozenDeliveryQuoteSummary;
  tabs: FrozenDeliveryQuoteBoxTab[];
}) {
  const resolvedSelectedTabIndex = Math.max(
    0,
    Math.min(selectedTabIndex, tabs.length - 1),
  );
  const selectedTab = tabs[resolvedSelectedTabIndex] ?? tabs[0] ?? null;
  const previewSmoothingPercent = selectedTab?.box?.items.some(
    (item) => item.id === "side",
  )
    ? 100
    : DEFAULT_PREVIEW_SMOOTHING_PERCENT;
  const deliveryResultCopy = buildFrozenDeliveryResultCopy(summary);

  return (
    <section className="overflow-hidden rounded-[2.25rem] border border-slate-200/80 bg-white text-slate-950 shadow-[0_28px_100px_-70px_rgba(15,23,42,0.65)]">
      <div className="grid gap-8 p-5 sm:p-7 lg:grid-cols-[minmax(18rem,0.72fr)_minmax(0,1.28fr)] lg:p-9">
        <div className="flex flex-col justify-between gap-7">
          <div>
            <p className="text-[1.26rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Delivery total
            </p>
            <p
              aria-live="polite"
              className="mt-3 text-[clamp(2.4rem,5.1vw,4.75rem)] font-semibold leading-none tracking-tight"
            >
              {summary.formattedDeliveryCost ?? "Unavailable"}
            </p>
            {deliveryResultCopy ? (
              <div className="mt-4 max-w-md rounded-[1.25rem] border border-slate-200 bg-slate-50/90 px-4 py-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.18)]">
                <p className="text-[0.98rem] font-medium leading-7 text-slate-800 sm:text-[1.03rem]">
                  {deliveryResultCopy.primary}
                </p>
                {deliveryResultCopy.secondary ? (
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {deliveryResultCopy.secondary}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <SummaryMetric label="Boxes" value={String(summary.boxesNeeded)} />
            <SummaryMetric label="Space left" value={summary.spareLabel} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <button
              ref={addItemsButtonRef}
              type="button"
              onClick={onOpenItems}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[var(--storefront-primary)] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-[0_20px_44px_-24px_rgba(13,89,242,0.68)] transition-colors hover:bg-slate-950"
            >
              Add items
            </button>
            <button
              type="button"
              onClick={onStartAgain}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-slate-700 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-950"
            >
              Start again
            </button>
          </div>
        </div>

        <div className="rounded-[1.8rem] border border-slate-200 bg-slate-50/90 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.92)] sm:p-5">
          <div>
            <div>
              <p className="text-[1.26rem] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Packed boxes
              </p>
              <p className="mt-1 text-[1.08rem] font-semibold leading-7 text-slate-800 sm:text-[1.14rem]">
                {summary.hasItems
                  ? summary.capacityLine
                  : "Your first frozen box is ready to fill."}
              </p>
            </div>
            {tabs.length > 0 ? (
              <div className="frozen-delivery-product-rail frozen-delivery-tab-strip -mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1">
                {tabs.map((tab, index) => (
                  <button
                    key={`${tab.label}-${tab.boxIndex ?? "new"}`}
                    type="button"
                    onClick={() => onSelectTab(index)}
                    aria-pressed={index === resolvedSelectedTabIndex}
                    className={`frozen-delivery-tab shrink-0 ${
                      index === resolvedSelectedTabIndex
                        ? "frozen-delivery-tab-active"
                        : "frozen-delivery-tab-inactive"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
            {/*
            {summary.hasItems ? (
              <div className="mb-4">
                <div
                  id="frozen-delivery-visual-smoothing-panel"
                  className="rounded-[1.1rem] border border-slate-200 bg-white px-4 py-3 shadow-[0_16px_30px_-28px_rgba(15,23,42,0.35)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="frozen-delivery-visual-smoothing"
                      className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-slate-400"
                    >
                      Visual smoothing
                    </label>
                    <span
                      id="frozen-delivery-visual-smoothing-value"
                      className="text-sm font-semibold text-slate-700"
                    >
                      {previewSmoothingPercent}% smoothing
                    </span>
                  </div>
                  <input
                    id="frozen-delivery-visual-smoothing"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={previewSmoothingPercent}
                    onChange={(event) =>
                      onPreviewSmoothingPercentChange(
                        Number(event.currentTarget.value),
                      )
                    }
                    aria-describedby="frozen-delivery-visual-smoothing-value"
                    className="mt-3 w-full cursor-pointer accent-[var(--storefront-primary)]"
                  />
                </div>
              </div>
            ) : null}
            */}
          </div>

          <FrozenDeliveryQuoteActiveTabPanel
            previewSmoothingPercent={previewSmoothingPercent}
            selectedTab={selectedTab}
            onRemoveItem={onRemoveItem}
          />
        </div>
      </div>
    </section>
  );
}

function FrozenDeliveryQuoteActiveTabPanel({
  previewSmoothingPercent,
  selectedTab,
  onRemoveItem,
}: {
  previewSmoothingPercent: number;
  selectedTab: FrozenDeliveryQuoteBoxTab | null;
  onRemoveItem: (productId: FrozenDeliveryQuoteProductId) => void;
}) {
  const selectedBox = selectedTab?.box ?? null;
  const emptyMessage = selectedTab?.label
    ? `${selectedTab.label} is ready to fill. Add items to start packing it.`
    : "Box 1 is ready to fill. Add items to start packing it.";

  return (
    <div className="frozen-delivery-active-box-panel mt-4">
      <div className="frozen-delivery-active-box-body">
        <FrozenDeliveryQuoteBoxPreview
          box={selectedBox}
          emptyMessage={emptyMessage}
          onRemoveItem={onRemoveItem}
          smoothingStrength={previewSmoothingPercent / 100}
        />
      </div>
    </div>
  );
}

function FrozenDeliveryItemsModal({
  activeProductIndex,
  closeButtonRef,
  isPending,
  onActiveProductIndexChange,
  onAddItem,
  onClose,
  quantities,
}: {
  activeProductIndex: number;
  closeButtonRef: RefObject<HTMLButtonElement | null>;
  isPending: boolean;
  onActiveProductIndexChange: (index: number) => void;
  onAddItem: (productId: FrozenDeliveryQuoteProductId) => void;
  onClose: () => void;
  quantities: FrozenDeliveryQuoteQuantities;
}) {
  const activeProduct = FROZEN_DELIVERY_QUOTE_PRODUCTS[activeProductIndex];
  const canGoPrevious = activeProductIndex > 0;
  const canGoNext = activeProductIndex < FROZEN_DELIVERY_QUOTE_PRODUCTS.length - 1;
  const navigateToPreviousProduct = () =>
    onActiveProductIndexChange(Math.max(0, activeProductIndex - 1));
  const navigateToNextProduct = () =>
    onActiveProductIndexChange(
      Math.min(FROZEN_DELIVERY_QUOTE_PRODUCTS.length - 1, activeProductIndex + 1),
    );

  function handleBackdropClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/72 px-4 py-5 backdrop-blur-md"
      onMouseDown={handleBackdropClick}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="frozen-delivery-items-title"
        className="flex max-h-[min(46rem,92vh)] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] bg-white text-slate-950 shadow-[0_32px_120px_-52px_rgba(0,0,0,0.9)]"
      >
        <div className="shrink-0 flex items-start justify-between gap-5 border-b border-slate-200 px-5 py-4 sm:px-7">
          <div>
            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-[var(--storefront-primary)]">
              Add items
            </p>
            <h2
              id="frozen-delivery-items-title"
              className="mt-1 text-2xl font-semibold tracking-tight text-slate-950"
            >
              Choose an item to add to your order.
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xl font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
            aria-label="Close item selector"
          >
            x
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
          <div className="relative overflow-hidden">
            <div className="mb-4 flex items-center justify-between gap-4 sm:hidden">
              <button
                type="button"
                onClick={navigateToPreviousProduct}
                disabled={!canGoPrevious}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_16px_30px_-18px_rgba(13,89,242,0.75)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] disabled:pointer-events-none disabled:opacity-25"
                aria-label="Previous product"
              >
                <ArrowIcon direction="left" />
              </button>
              <p className="text-center text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-slate-400">
                Browse item types
              </p>
              <button
                type="button"
                onClick={navigateToNextProduct}
                disabled={!canGoNext}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_16px_30px_-18px_rgba(13,89,242,0.75)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] disabled:pointer-events-none disabled:opacity-25"
                aria-label="Next product"
              >
                <ArrowIcon direction="right" />
              </button>
            </div>

            <div className="px-1 transition-opacity duration-200 ease-out">
              <FrozenDeliveryModalProductCard
                key={activeProduct.id}
                isPending={isPending}
                onAddItem={onAddItem}
                product={activeProduct}
                quantity={quantities[activeProduct.id] ?? 0}
              />
            </div>

            <button
              type="button"
              onClick={navigateToPreviousProduct}
              disabled={!canGoPrevious}
              className="absolute left-2 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_16px_30px_-18px_rgba(13,89,242,0.75)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] disabled:pointer-events-none disabled:opacity-25 sm:inline-flex"
              aria-label="Previous product"
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              type="button"
              onClick={navigateToNextProduct}
              disabled={!canGoNext}
              className="absolute right-2 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_16px_30px_-18px_rgba(13,89,242,0.75)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] disabled:pointer-events-none disabled:opacity-25 sm:inline-flex"
              aria-label="Next product"
            >
              <ArrowIcon direction="right" />
            </button>
          </div>

        </div>

        <div className="flex shrink-0 flex-col gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold uppercase tracking-[0.14em] text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
          >
            Done
          </button>
        </div>
      </section>
    </div>
  );
}

function FrozenDeliveryModalProductCard({
  onAddItem,
  isPending,
  product,
  quantity,
}: FrozenDeliveryQuoteProductCardProps) {
  return (
    <article className="grid min-h-[24rem] gap-6 rounded-[1.6rem] border border-slate-200 bg-white p-5 sm:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] sm:p-6">
      <div className="relative flex min-h-64 items-center justify-center overflow-hidden rounded-[1.35rem] border border-slate-100 bg-white">
        <Image
          alt={product.imageAlt}
          className="h-auto max-h-56 w-auto max-w-[82%] object-contain"
          height={260}
          src={product.imageSrc}
          width={320}
        />
      </div>

      <div className="flex flex-col justify-between">
        <div>
          <span className="inline-flex rounded-full bg-slate-950 px-3 py-1.5 text-xs font-bold text-white">
            {product.units > 20
              ? "This item needs 2 boxes."
              : `This item takes up ${product.capacityLabel} in a box.`}
          </span>
          <h3 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
            {product.name}
          </h3>
          <ProductCapacitySentence product={product} />
          <p className="mt-4 rounded-[1rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold leading-6 text-slate-800">
            {product.fitHint}
          </p>
        </div>

        <div
          className={`mt-8 flex flex-wrap items-center justify-between gap-3 rounded-[1.2rem] border border-slate-200 bg-slate-50 px-4 py-4 ${
            isPending ? "opacity-90" : ""
          }`}
        >
          <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
            {quantity} in your order
          </span>
          <button
            type="button"
            onClick={() => onAddItem(product.id)}
            className="inline-flex min-h-12 items-center justify-center rounded-full bg-[var(--storefront-primary)] px-6 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-slate-950"
            aria-label={`Add ${product.name}`}
          >
            Add item
          </button>
        </div>
      </div>
    </article>
  );
}

function ProductCapacitySentence({
  product,
}: {
  product: FrozenDeliveryQuoteProduct;
}) {
  return (
    <p
      aria-label={product.customerDescription}
      className="mt-2 text-base leading-7 text-slate-500"
    >
      A {product.name}{" "}
      <span className="font-semibold text-slate-950">
        takes up {product.capacityLabel}
      </span>
      .
    </p>
  );
}

function capacityStackForUnits(units: number) {
  return {
    preface: "takes up",
    detail: "of the space",
    value: `${capacityPercentForUnits(units)}%`,
  };
}

function SummaryMetric({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[1.25rem] border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.22)]">
      <p className="text-[1.22rem] font-semibold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold tracking-tight text-slate-950">
        {value}
      </p>
    </div>
  );
}

function FrozenDeliveryQuoteBoxPreview({
  box,
  emptyMessage,
  onRemoveItem,
  smoothingStrength,
}: {
  box: FrozenDeliveryQuoteBox | null;
  emptyMessage: string;
  onRemoveItem: (productId: FrozenDeliveryQuoteProductId) => void;
  smoothingStrength: number;
}) {
  const desktopLayout = box
    ? buildFrozenDeliveryQuoteBoxPreviewLayout(box, "desktop", {
        smoothingStrength,
      })
    : null;
  const mobileLayout = box
    ? buildFrozenDeliveryQuoteBoxPreviewLayout(box, "mobile", {
        smoothingStrength,
      })
    : null;
  const boxShellStyle = {
    "--frozen-delivery-box-image": `url("${POLY_BOX_IMAGE_SRC}")`,
    ...(desktopLayout && mobileLayout
      ? {
          "--frozen-delivery-box-height": `${desktopLayout.boxVisualHeightRem}rem`,
          "--frozen-delivery-box-height-mobile": `${mobileLayout.boxVisualHeightRem}rem`,
        }
      : {}),
  } as CSSProperties;

  return (
    <article className="min-w-0">
      <div
        className={`frozen-delivery-box-shell ${
          box ? "" : "frozen-delivery-box-shell-empty"
        }`}
        style={boxShellStyle}
      >
        {box ? (
          <>
            {box.items.map((item, pieceIndex) => {
              const desktopSliceLayout = desktopLayout?.slices[pieceIndex];
              const mobileSliceLayout = mobileLayout?.slices[pieceIndex];
              const itemStyle = {
                "--frozen-delivery-slice-height": `${desktopSliceLayout?.heightRem ?? 0}rem`,
                "--frozen-delivery-slice-height-mobile": `${mobileSliceLayout?.heightRem ?? 0}rem`,
                "--frozen-delivery-slice-share": String(
                  desktopSliceLayout?.visualShare ?? 0,
                ),
              } as CSSProperties;
              const capacityStack = capacityStackForUnits(item.units);
              const imageClassName =
                item.short === "Side"
                  ? "object-contain scale-[1.88]"
                  : "object-contain scale-[1.46]";

              return (
                <div
                  key={`${item.name}-${pieceIndex}`}
                  className="frozen-delivery-photo-pack-item"
                  style={itemStyle}
                  title={`${item.name}, ${capacityLabelForUnits(item.units)}`}
                >
                  <span className="frozen-delivery-photo-pack-image">
                    <Image
                      alt=""
                      className={imageClassName}
                      fill
                      sizes="96px"
                      src={item.imageSrc}
                    />
                  </span>
                  <span className="frozen-delivery-photo-pack-copy">
                    <strong>{item.short}</strong>
                  </span>
                  <span className="frozen-delivery-photo-pack-capacity">
                    <span className="frozen-delivery-photo-pack-capacity-detail">
                      {capacityStack.preface}
                    </span>
                    <span className="frozen-delivery-photo-pack-capacity-value">
                      {capacityStack.value}
                    </span>
                    <span className="frozen-delivery-photo-pack-capacity-detail">
                      {capacityStack.detail}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveItem(item.id)}
                      className="frozen-delivery-photo-pack-remove"
                    >
                      Remove From Box
                    </button>
                  </span>
                </div>
              );
            })}
            {box.remainingUnits > 0 ? (
              <div
                className="frozen-delivery-box-free-space"
                style={
                  {
                    "--frozen-delivery-free-space-height": `${desktopLayout?.freeSpaceHeightRem ?? 0}rem`,
                    "--frozen-delivery-free-space-height-mobile": `${mobileLayout?.freeSpaceHeightRem ?? 0}rem`,
                  } as CSSProperties
                }
              >
                {box.freePercent}% free
              </div>
            ) : null}
          </>
        ) : (
          <div className="max-w-[16rem] text-center text-sm leading-6 text-slate-500">
            {emptyMessage}
          </div>
        )}
      </div>
      {box ? (
        <div
          className="frozen-delivery-box-meter"
          aria-label={`Box ${box.usedPercent}% Full, ${box.freePercent}% Space Left`}
        >
          <div className="frozen-delivery-box-meter-track">
            <span
              className="frozen-delivery-box-meter-fill"
              style={{ width: `${box.usedPercent}%` }}
            />
          </div>
          <div className="frozen-delivery-box-meter-labels">
            <span>{`Box ${box.usedPercent}% Full`}</span>
            <span>{`${box.freePercent}% Space Left`}</span>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function findFirstSelectedProductIndex(quantities: FrozenDeliveryQuoteQuantities) {
  const selectedIndex = FROZEN_DELIVERY_QUOTE_PRODUCTS.findIndex(
    (product) => (quantities[product.id] ?? 0) > 0,
  );

  return selectedIndex >= 0 ? selectedIndex : 0;
}
